import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { googleLogout } from "@react-oauth/google";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import InstallAppButton from "./InstallAppButton";

const AppMenu = ({ variant = "header" }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { t } = useLanguage();

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!menuRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleLogout = () => {
    googleLogout();
    logout();
    setOpen(false);
  };

  const goOrSignIn = (path) => {
    setOpen(false);
    navigate(user ? path : "/profile");
  };

  return (
    <Wrap ref={menuRef} $variant={variant}>
      <Trigger
        type="button"
        $variant={variant}
        onClick={() => setOpen((v) => !v)}
        aria-label={t("set.title")}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M7 4v16M12 4v16M17 4v16"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
          <circle cx="7" cy="10" r="2.45" fill="currentColor" />
          <circle cx="12" cy="15.2" r="2.45" fill="currentColor" />
          <circle cx="17" cy="8.2" r="2.45" fill="currentColor" />
        </svg>
      </Trigger>

      {open && (
        <Dropdown role="menu" $variant={variant}>
          <ToggleRow>
            <span>{t("set.dark")}</span>
            <Switch
              type="button"
              role="switch"
              aria-checked={isDark}
              $on={isDark}
              onClick={toggleTheme}
            >
              <Knob $on={isDark} />
            </Switch>
          </ToggleRow>

          <Divider />

          <Item as={Link} to="/about" onClick={() => setOpen(false)}>
            {t("set.about")}
          </Item>
          <Item as="button" type="button" onClick={() => goOrSignIn("/profile")}>
            {t("set.profile")}
          </Item>
          <Item as="button" type="button" onClick={() => goOrSignIn("/favourites")}>
            {t("set.share")}
          </Item>
          <Item as="button" type="button" onClick={() => goOrSignIn("/pool")}>
            {t("set.join")}
          </Item>
          <Item as="button" type="button" onClick={() => goOrSignIn("/friends")}>
            {t("set.friends")}
          </Item>
          <InstallAppButton asMenuItem />
          {user ? (
            <>
              <Divider />
              <Item as="button" type="button" onClick={handleLogout}>
                {t("set.logout")}
              </Item>
            </>
          ) : null}
        </Dropdown>
      )}
    </Wrap>
  );
};

export default AppMenu;

const Wrap = styled.div`
  position: relative;
  z-index: 80;
  flex-shrink: 0;
`;

const Trigger = styled.button`
  width: ${(p) => (p.$variant === "hero" ? "42px" : "40px")};
  height: ${(p) => (p.$variant === "hero" ? "42px" : "40px")};
  display: grid;
  place-items: center;
  padding: 0;
  border-radius: 50%;
  color: ${(p) => (p.$variant === "hero" ? "#f7f3ec" : "inherit")};
  background: ${(p) =>
    p.$variant === "hero" ? "rgba(15, 15, 16, 0.55)" : "transparent"};
  border: 1px solid
    ${(p) =>
      p.$variant === "hero" ? "rgba(247, 243, 236, 0.28)" : "currentColor"};
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 228px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 14px;
  box-shadow: var(--shadow-soft);
  display: flex;
  flex-direction: column;
  padding: 6px;
  z-index: 100;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 11px 12px;
  color: var(--ink);
  font-weight: 600;
  font-size: 14px;
`;

const Switch = styled.button`
  width: 44px;
  height: 26px;
  border-radius: 999px;
  border: none;
  padding: 2px;
  display: flex;
  align-items: center;
  background: ${(p) => (p.$on ? "var(--color-yellow)" : "var(--color-stone)")};
  flex-shrink: 0;
`;

const Knob = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  display: block;
  transform: translateX(${(p) => (p.$on ? "18px" : "0")});
  transition: transform var(--duration) var(--ease);
  box-shadow: 0 1px 2px rgba(15, 15, 16, 0.2);
`;

const Item = styled.a`
  padding: 11px 12px;
  text-align: left;
  text-decoration: none;
  background: none;
  border: none;
  color: var(--ink);
  font-weight: 600;
  font-size: 14px;
  border-radius: 10px;
  cursor: pointer;
  width: 100%;
  font-family: var(--font-body);

  &:hover {
    background: var(--surface-2);
  }
`;

const Divider = styled.div`
  height: 1px;
  background: var(--line);
  margin: 4px 6px;
`;
